import { Box, createStyles, makeStyles, TextField, Typography } from '@material-ui/core';
import { TFunction } from 'i18next';
import { clone } from 'ramda';
import { groupBy } from 'ramda';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CONSTANTS } from '../../../../../core/config/constants';
import { toCssValue } from '../../../../../core/utils/theme.utils';
import { useFilterState } from '../../../shared/FinancialDataContext/FinancialDataContext';
import {
  AppliedFilterInterface,
  LibraryFilterItem,
} from '../../../shared/FinancialDataContext/FinancialDataContext.types';
import FilterLibraryRootItem from './FilterLibraryRootItem';

export interface AddRemoveFilter {
  isFilterAdded: boolean;
  filter: LibraryFilterItem;
}

const groupByParent = groupBy((filter: any) => filter.parent);

const groupFilters = (appliedFilters: AppliedFilterInterface[], flattenedFilters: any) => {
  const mappedAppliedFilters: LibraryFilterItem[] = appliedFilters.map(
    (labelKey) => flattenedFilters[labelKey],
  );

  return groupByParent(mappedAppliedFilters);
};

const FilterLibrary: React.FC = React.memo(() => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { appliedFilters, libraryFilters, flattenedLibraryFilters } = useFilterState();
  const [groupedByParent, setGroupedFilters] = useState<{
    [key: string]: LibraryFilterItem[];
  }>({});
  const [filteredFilters, setFilteredFilters] = useState(libraryFilters);
  const [filterSearchValue, setFilterSearchValue] = useState('');

  useEffect(() => {
    setGroupedFilters(groupFilters(appliedFilters, flattenedLibraryFilters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  useEffect(() => {
    if (!filterSearchValue) {
      setFilteredFilters(libraryFilters);
    } else {
      setFilteredFilters(filterLibraryFiltersByName(clone(libraryFilters), filterSearchValue, t));
    }
  }, [filterSearchValue, libraryFilters, t]);

  const renderedFilters = useMemo(() => {
    return (
      <>
        {filteredFilters.map((filter: LibraryFilterItem, index: number) => (
          <FilterLibraryRootItem
            key={`root-${filter.labelKey}`}
            filterItem={filter}
            index={index}
            activeCount={groupedByParent[filter.labelKey]?.length}
          />
        ))}
        {!filteredFilters?.length && (
          <Typography className={classes.noFilterFoundLabel}>
            {t('companies:noFilterFound')}
          </Typography>
        )}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredFilters, groupedByParent]);

  return (
    <>
      <Box className={classes.filterSearchBox}>
        <TextField
          value={filterSearchValue}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFilterSearchValue(event.target.value);
          }}
          className={classes.filterSearchInput}
          placeholder={t('companies:filterSearch')}
          variant="outlined"
        />
      </Box>
      {renderedFilters}
    </>
  );
});

const useStyles = makeStyles((theme) =>
  createStyles({
    filterSearchBox: {
      width: '100%',
      display: 'flex',
      height: CONSTANTS.TABLE_HEADER,
      backgroundColor: theme.palette.secondary.main,
      borderLeft: `1px solid ${theme.palette.divider}`,
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: toCssValue([theme.spacing(3), theme.spacing(4)]),
    },
    filterSearchInput: {
      '& .MuiInputBase-root': {
        height: '100%',
      },
      '& input': {
        height: '100%',
      },
    },
    noFilterFoundLabel: {
      padding: theme.spacing(3),
    },
  }),
);

function filterLibraryFiltersByName(
  filters: LibraryFilterItem[],
  searchValue: string,
  t: TFunction,
): LibraryFilterItem[] {
  return filters.filter((filter) => {
    if (!filter.children?.length) {
      return t(`companies:filters.${filter.labelKey}`)
        .toLowerCase()
        .includes(searchValue.toLowerCase());
    }

    const filteredChildren = filterLibraryFiltersByName(filter.children, searchValue, t);

    if (!filteredChildren?.length) return false;

    filter.children = filteredChildren;
    return true;
  });
}

export default FilterLibrary;
